/**
 * A record's note log, and the one field that adds to it — decision 91.
 *
 * Drop it into any record form. It owns its own subscription and its own write,
 * so a feature adds notes by rendering one component rather than by growing a
 * repository, a piece of state and a textarea of its own — which is how the five
 * lists ended up with five different note affordances, one of which was a prose
 * blob and three of which were nothing.
 *
 * ## The empty state names the cause, not the absence
 *
 * Decision 23 bans "No notes". The line says what fills it instead, which on a
 * log is genuinely useful: somebody looking at a partner they have never spoken
 * to should be told that is what they are looking at.
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { formatDate } from '@/shared/util/dates'
import { addNote, migrateProseNote, subscribeToNotes } from './notesRepository'
import { NoteComposer } from './NoteComposer'
import { type Note, type NoteKind } from './notes'
import './NotesField.css'

export function NotesField({
  kind,
  recordId,
  legend = 'Notes',
  hint,
  prose,
}: {
  kind: NoteKind
  /** Empty while a record is being created — the log needs something to hang off. */
  recordId: string
  legend?: string
  /** One line under the legend, where the kind needs a caveat (91: shareholders). */
  hint?: string
  /**
   * The record's old single `notes` string, where it had one.
   *
   * Carried into the log as its first entry the first time somebody opens the
   * record, rather than in a migration script — the string is only reachable
   * from the record that owns it, and a script would need to know every shape.
   */
  prose?: string
}) {
  const { user } = useAuth()
  const [notes, setNotes] = useState<Note[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!recordId) {
      setNotes([])
      return
    }
    setNotes(null)
    setError(null)
    return subscribeToNotes(
      kind,
      recordId,
      (rows) => setNotes(rows),
      (err) => {
        setNotes([])
        setError(err.message)
      },
    )
  }, [kind, recordId])

  /**
   * The prose field becomes the log's first entry, once.
   *
   * Guarded on the log being loaded *and* empty, so it cannot run twice and
   * cannot run before the subscription has told us what is already there. A
   * record whose log has anything in it has already been migrated, which is why
   * this needs no flag on the record itself.
   */
  useEffect(() => {
    if (!recordId || notes === null || notes.length > 0) return
    const trimmed = (prose ?? '').trim()
    if (!trimmed) return
    void migrateProseNote(kind, recordId, trimmed).catch(() => {
      // A failure here loses nothing: the prose is still on the record and this
      // runs again the next time somebody opens it.
    })
  }, [kind, recordId, notes, prose])


  return (
    <section className="recform__group">
      <h4 className="recform__legend">
        {legend}
        {notes && notes.length > 0 && <span className="notes__count"> · {notes.length}</span>}
      </h4>
      {hint && <p className="recform__note">{hint}</p>}

      {!recordId ? (
        <p className="empty-note">
          Notes open once the record is saved — a note has to hang off something.
        </p>
      ) : (
        <>
          <NoteComposer
            onSave={(t) =>
              addNote(kind, recordId, user?.displayName || user?.email || 'Unknown', t)
            }
          />
          {error && <p className="recform__error">{error}</p>}

          {notes === null && <p className="empty-note">Loading the log…</p>}

          {notes !== null && notes.length === 0 && (
            <p className="empty-note">
              Nothing recorded here yet. Notes appear as people add them, newest first, each with
              the date and who wrote it.
            </p>
          )}

          {notes !== null && notes.length > 0 && (
            <ul className="notes__list">
              {notes.map((n) => (
                <li className="notes__item" key={n.id}>
                  <div className="notes__meta">
                    <span className="notes__author">{n.author}</span>
                    <span className="notes__at">{formatDate(n.at.slice(0, 10))}</span>
                    {/* Says why this one has no real author or date — 91. */}
                    {n.source === 'migrated' && <span className="notes__tag">carried over</span>}
                  </div>
                  <div className="notes__text">{n.text}</div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
